"use client";

import { ChartArea, Plus, Settings, Sheet, TrendingUp } from "lucide-react";
import { Card, CardBody, Link } from "@heroui/react";
import { TabKey } from "@/pages/Overview.tsx";

export const Main = ({
  maleAnimals,
  femaleAnimals,
  totalAnimals,
  setSelected,
  onViewAnimalsClick,
  onViewAnalyticsClick,
}: {
  maleAnimals: number;
  femaleAnimals: number;
  totalAnimals: number;
  setSelected: (key: TabKey) => void;
  onViewAnimalsClick: () => void;
  onViewAnalyticsClick: () => void;
}) => {
  return (
    <div className="min-h-screen w-full overflow-hidden bg-gradient-to-br from-slate-50 via-yellow-50/30 to-green-50/20 relative">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-yellow-200/20 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-200/20 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
        <div className="absolute top-40 left-40 w-80 h-80 bg-green-200/20 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-4000"></div>
      </div>

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fillRule=\'evenodd\'%3E%3Cg fill=\'%239C92AC\' fillOpacity=\'0.05\'%3E%3Ccircle cx=\'30\' cy=\'30\' r=\'1\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-40"></div>
      <div className="relative z-10 min-h-screen font-['suse'] flex flex-col">
        {/* Header Section */}
        <div className="flex-1 flex flex-col justify-center items-center px-4">
          {/* Welcome Section */}
          <div className="text-center mb-12 animate-in slide-in-from-top duration-1000">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-yellow-100 to-green-100 rounded-full mb-6 animate-pulse border border-yellow-200/50">
              {/* <Sparkles className="w-10 h-10 text-yellow-600" /> */}
            </div>
            <h1 className="text-5xl md:text-7xl font-bold bg-gradient-to-r from-slate-700 via-green-700 to-blue-700 bg-clip-text text-transparent mb-4 animate-in slide-in-from-bottom duration-1000 delay-300">
              Farm Dashboard
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed animate-in slide-in-from-bottom duration-1000 delay-500">
              Manage your livestock, track performance, and streamline your farm
              operations with our advanced management system.
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 w-full max-w-4xl animate-in slide-in-from-bottom duration-1000 delay-700">
            <div className="bg-white/60 backdrop-blur-md rounded-2xl p-6 border border-yellow-200/30 hover:bg-white/70 hover:border-yellow-300/50 transition-all duration-300">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-600 text-sm">Total Animals</p>
                  <p className="text-3xl font-bold text-slate-800">
                    {totalAnimals}
                  </p>
                </div>
                <TrendingUp className="w-8 h-8 text-green-600" />
              </div>
            </div>
            <div className="bg-white/60 backdrop-blur-md rounded-2xl p-6 border border-blue-200/30 hover:bg-white/70 hover:border-blue-300/50 transition-all duration-300">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-600 text-sm">Male</p>
                  <p className="text-3xl font-bold text-slate-800">
                    {maleAnimals}
                  </p>
                </div>
                <Settings className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            <div className="bg-white/60 backdrop-blur-md rounded-2xl p-6 border border-green-200/30 hover:bg-white/70 hover:border-green-300/50 transition-all duration-300">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-600 text-sm">Female</p>
                  <p className="text-3xl font-bold text-slate-800">
                    {femaleAnimals}
                  </p>
                </div>
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center border border-green-200">
                  <div className="w-4 h-4 bg-green-600 rounded-full"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Action Cards */}
          <div className="flex flex-col lg:flex-row gap-8 mb-8 animate-in slide-in-from-bottom duration-1000 delay-1000">
            <Card
              isPressable
              className="group relative overflow-hidden bg-gradient-to-br from-yellow-50/80 to-yellow-100/60 backdrop-blur-md border border-yellow-200/40 hover:border-yellow-300/60 transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-yellow-200/25 w-72 h-80"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-yellow-100/20 to-yellow-200/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <CardBody
                as={Link}
                className="relative z-10 overflow-visible p-8 h-full flex flex-col justify-center items-center text-center"
                href="/add-animal"
              >
                <div className="relative mb-6">
                  <div className="absolute inset-0 bg-yellow-200/30 rounded-full blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-500 animate-pulse"></div>
                  <div className="relative bg-gradient-to-br from-yellow-100 to-yellow-200 p-6 rounded-full group-hover:scale-110 transition-transform duration-500 border border-yellow-300/50">
                    <Plus className="text-yellow-700" size={48} />
                  </div>
                </div>
                <div className="space-y-2">
                  <span className="text-2xl font-bold text-slate-800 group-hover:text-yellow-800 transition-colors duration-300">
                    Register Animals
                  </span>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Add new animals to your farm registry with complete health
                    records and genealogy information.
                  </p>
                </div>
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-gradient-to-r from-yellow-300 to-yellow-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </CardBody>
            </Card>
            {/* Registered Animals Card */}
            <Card
              isPressable
              onPress={onViewAnimalsClick}
              className="group relative overflow-hidden bg-gradient-to-br from-blue-50/80 to-blue-100/60 backdrop-blur-md border border-blue-200/40 hover:border-blue-300/60 transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-blue-200/25 w-72 h-80"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-blue-100/20 to-blue-200/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <CardBody
                // as={Link}
                className="relative z-10 overflow-visible p-8 h-full flex flex-col justify-center items-center text-center"
                // href="/overview"
                onClick={() => setSelected("Animals")}
              >
                <div className="relative mb-6">
                  <div className="absolute inset-0 bg-blue-200/30 rounded-full blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-500 animate-pulse"></div>
                  <div className="relative bg-gradient-to-br from-blue-100 to-blue-200 p-6 rounded-full group-hover:scale-110 transition-transform duration-500 border border-blue-300/50">
                    <Sheet className="text-blue-700" size={48} />
                  </div>
                </div>
                <div className="space-y-2">
                  <h3 className="text-2xl font-bold text-slate-800 group-hover:text-blue-800 transition-colors duration-300">
                    Registered Animals
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    View and manage all your registered livestock with detailed
                    records and health tracking.
                  </p>
                </div>
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-gradient-to-r from-blue-300 to-blue-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </CardBody>
            </Card>

            {/* Analytics Card */}
            <Card
              isPressable
              onClick={onViewAnalyticsClick}
              className="group relative overflow-hidden bg-gradient-to-br from-orange-50/80 to-orange-100/60 backdrop-blur-md border border-purple-200/40 hover:border-orange-300/60 transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-green-200/25 w-72 h-80"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-orange-100/20 to-orange-200/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <CardBody
                className="relative z-10 overflow-visible p-8 h-full flex flex-col justify-center items-center text-center"
                onClick={() => setSelected("Analytics")}
              >
                <div className="relative mb-6">
                  <div className="absolute inset-0 bg-orange-200/30 rounded-full blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-500 animate-pulse"></div>
                  <div className="relative bg-gradient-to-br from-orange-100 to-orange-200 p-6 rounded-full group-hover:scale-110 transition-transform duration-500 border border-orange-300/50">
                    <ChartArea className="text-orange-700" size={48} />
                  </div>
                </div>
                <div className="space-y-2">
                  <h3 className="text-2xl font-bold text-slate-800 group-hover:text-puple-800 transition-colors duration-300">
                    Analytics
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Configure your farm preferences, user settings, and system
                    configurations.
                  </p>
                </div>
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-gradient-to-r from-orange-300 to-orange-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </CardBody>
            </Card>
            <Card
              isPressable
              className="group relative overflow-hidden bg-gradient-to-br from-green-50/80 to-green-100/60 backdrop-blur-md border border-green-200/40 hover:border-green-300/60 transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-green-200/25 w-72 h-80"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-green-100/20 to-green-200/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <CardBody
                as={Link}
                className="relative z-10 overflow-visible p-8 h-full flex flex-col justify-center items-center text-center"
                href="/settings"
              >
                <div className="relative mb-6">
                  <div className="absolute inset-0 bg-green-200/30 rounded-full blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-500 animate-pulse"></div>
                  <div className="relative bg-gradient-to-br from-green-100 to-green-200 p-6 rounded-full group-hover:scale-110 transition-transform duration-500 group-hover:rotate-12 border border-green-300/50">
                    <Settings className="text-green-700" size={48} />
                  </div>
                </div>
                <div className="space-y-2">
                  <h3 className="text-2xl font-bold text-slate-800 group-hover:text-green-800 transition-colors duration-300">
                    Settings
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    Configure your farm preferences, user settings, and system
                    configurations.
                  </p>
                </div>
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-gradient-to-r from-green-300 to-green-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>


      <style>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};
